/**
 * The award objects, from the badge spec sheet in the profile/awards mockup.
 *
 * There are exactly two kinds, and keeping them distinguishable is the point:
 *
 *   `ServiceTile` — tenure. A **numeral** in an ogival crest, because tenure is a
 *   calendar fact and a number states it precisely. Every tier renders the same
 *   crest silhouette; rank is carried purely by metal (see awardMetals.js), NOT by
 *   a fourth silhouette — a different shape per rank would read as a different
 *   *kind* of thing rather than a higher one. Two-tone stops (the transitions
 *   between solid metals) render as a dual ring: the plate and inner hairline are
 *   the base metal, the outer burnished rim is the accent metal.
 *
 *   `Medallion` — everything else (credentials, achievements). A circular tile with
 *   a line mark. Round vs squared is what stops a licence being mistaken for a
 *   service award at a glance. Also the home of the separate steel/brass scale
 *   used for certification badges (permanent vs renewal-dated licence) — unrelated
 *   to the tenure ramp, sharing this file only because both are "metal tiles".
 *
 * Both take a pixel size, because the same object has to survive four very
 * different renders — 16px in a row, 22px in a chip stack, 40px in a card, 96 or
 * 112px in the earn moment. At 16px the numeral is a ~6px glyph, so it is dropped
 * and the tile becomes a pure mark; the number is carried by the adjacent tenure
 * chip, which is always present wherever a 16px tile appears.
 *
 * Unearned awards are dashed outlines, never a padlock emoji: a lock says "you are
 * not allowed", when the truth is "not yet".
 */

import { metalForYears } from './awardMetals';

const METALS = {
  steel: { background: 'var(--steel-face)', borderColor: 'var(--steel-ring)', color: 'var(--steel-ink)' },
  brass: { background: 'var(--brass-face)', borderColor: 'var(--brass-ring)', color: 'var(--brass-ink)' },
  bronze: { background: 'var(--bronze-face)', borderColor: 'var(--bronze-ring)', color: 'var(--bronze-ink)' },
  silver: { background: 'var(--silver-face)', borderColor: 'var(--silver-ring)', color: 'var(--silver-ink)' },
  gold: {
    background: 'var(--gold-face)',
    borderColor: 'var(--gold-ring)',
    color: 'var(--gold-ink)',
    boxShadow: '0 0 0 1px var(--gold-ring) inset',
  },
  onyx: { background: 'var(--onyx-face)', borderColor: 'var(--onyx-ring)', color: 'var(--onyx-ink)' },
  platinum: { background: 'var(--platinum-face)', borderColor: 'var(--platinum-ring)', color: 'var(--platinum-ink)' },
  obsidian: { background: 'var(--obsidian-face)', borderColor: 'var(--obsidian-ring)', color: 'var(--obsidian-ink)' },
  diamond: { background: 'var(--diamond-face)', borderColor: 'var(--diamond-ring)', color: 'var(--diamond-ink)' },
  /** Earned but unranked — brand teal, deliberately not a metal. */
  brand: { background: 'var(--accent-soft)', borderColor: 'var(--accent-line)', color: 'var(--accent)' },
};

const STATES = {
  locked: {
    background: 'transparent',
    borderColor: 'var(--line-strong)',
    borderStyle: 'dashed',
    color: 'var(--ink-3)',
    opacity: 0.8,
  },
  /** Reachable next — dashed, but brand-tinted so it reads as an invitation. */
  next: {
    background: 'transparent',
    borderColor: 'var(--accent-line)',
    borderStyle: 'dashed',
    color: 'var(--accent)',
  },
};

/** Corner radius grows with the tile, so a 112px tile is not a 16px tile scaled. */
function tileRadius(size) {
  if (size <= 16) return 4;
  if (size <= 22) return 6;
  if (size <= 40) return 10;
  if (size <= 56) return 12;
  if (size <= 96) return 18;
  return 22;
}

/**
 * The crest: squared shoulders, base drawn to a point, ~10% of the height hanging
 * below the tile baseline so it optically outranks a same-sized square beside it.
 * All escalations are outline facts, which is the only kind that survives a 16px
 * render. Ring stops scale with size rather than using fixed px, so the edge reads
 * as a hairline at 16px and a real ceremonial frame at 112px instead of going wiry.
 *
 * @param {{base: string, accent？: string}|null} metal Null for locked/next states.
 */
function crestStyle(size, state, metal) {
  const shoulder = size * 0.16;
  // Two-tone rims extend ~11% beyond the box (the accent band + its burnish), so
  // give them more breathing room than a solid crest needs, or the rim gets
  // shaved by a sibling or an ancestor's overflow.
  const marginPx = metal?.accent ? Math.max(6, size * 0.075) : Math.max(3, size * 0.05);
  const style = {
    borderRadius: `${shoulder}px ${shoulder}px 50% 50% / ${shoulder}px ${shoulder}px 80% 80%`,
    paddingBottom: size * 0.12,
    margin: `0 ${marginPx}px`,
  };
  if (state === 'earned' && metal) {
    const inner = size * 0.028;
    const gap = size * 0.038;
    const outer = gap + size * 0.048;
    const burnish = outer + size * 0.022;
    const rim = metal.accent || metal.base;
    style.boxShadow = `0 0 0 ${inner}px var(--${metal.base}-ring) inset, `
      + `0 0 0 ${gap}px var(--surface), `
      + `0 0 0 ${outer}px var(--${rim}-ring), `
      + `0 0 0 ${burnish}px var(--${rim}-deep)`;
  } else if (state === 'next') {
    style.boxShadow = '0 0 0 1.5px var(--surface), 0 0 0 2.5px var(--accent-line)';
  } else {
    style.boxShadow = '0 0 0 1.5px var(--surface), 0 0 0 2.5px var(--line-strong)';
  }
  return style;
}

/**
 * @param {object} props
 * @param {number} props.years Years of service — picks the metal AND is the payload.
 * @param {number} [props.size] Pixels: 16, 22, 40, 56, 96 or 112.
 * @param {'earned'|'locked'|'next'} [props.state]
 */
export function ServiceTile({ years, size = 40, state = 'earned', title, className = '' }) {
  const metal = metalForYears(years);
  const skin = state === 'earned' ? METALS[metal.base] : STATES[state];
  // At 16px a numeral is illegible, so the tile becomes a pure mark.
  const showNumeral = size > 16;
  const showUnit = size >= 40;
  // Dark plates (onyx, obsidian) carry their own dimmer sheen; the global --sheen
  // would blow out a highlight on a dark face. Solid metals fall back to it.
  const sheen = state === 'earned' ? `var(--${metal.base}-sheen, var(--sheen))` : null;

  return (
    <span
      title={title}
      className={`relative flex-none inline-flex flex-col items-center justify-center overflow-hidden
                  font-mono leading-none border ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: tileRadius(size),
        ...skin,
        ...crestStyle(size, state, state === 'earned' ? metal : null),
      }}
    >
      {/* Sheen: a single highlight, so the tile reads as a struck object rather than
          a coloured rectangle. Suppressed on unearned states — nothing to catch. */}
      {state === 'earned' && (
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ backgroundImage: `radial-gradient(120% 80% at 28% 8%, ${sheen}, transparent 60%)` }}
        />
      )}
      {showNumeral && (
        <span className="relative z-[1] font-bold tracking-[-0.02em]" style={{ fontSize: size * 0.38 }}>
          {years}
        </span>
      )}
      {showNumeral && showUnit && (
        <span
          className="relative z-[1] font-semibold uppercase tracking-[0.1em] opacity-70 mt-px"
          style={{ fontSize: size * 0.17 }}
        >
          {years === 1 ? 'year' : 'years'}
        </span>
      )}
    </span>
  );
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.children A line-art SVG mark, or an emoji fallback.
 * @param {'steel'|'brass'|'brand'} [props.metal]
 * @param {number} [props.size]
 * @param {'earned'|'locked'|'next'} [props.state]
 */
export function Medallion({ children, metal = 'brand', size = 40, state = 'earned', title, className = '' }) {
  const skin = state === 'earned' ? METALS[metal] : STATES[state];
  // At 16px the mark has to take more of the tile and thin its stroke, or it turns
  // into a dot. Only simple-geometry marks are legible there at all.
  const markSize = size <= 16 ? size * 0.58 : size * 0.46;

  return (
    <span
      title={title}
      className={`relative flex-none inline-flex items-center justify-center rounded-full border ${className}`}
      style={{ width: size, height: size, ...skin }}
    >
      {state === 'earned' && (
        <span
          aria-hidden
          className="absolute inset-px rounded-full pointer-events-none opacity-70"
          style={{ backgroundImage: 'radial-gradient(120% 90% at 30% 12%, var(--sheen), transparent 62%)' }}
        />
      )}
      <span
        className="relative z-[1] inline-flex items-center justify-center"
        style={{ width: markSize, height: markSize, fontSize: markSize * 0.9 }}
      >
        {children}
      </span>
    </span>
  );
}
